/**
 * TurnstileWidget — Cloudflare Turnstile en modo Managed.
 *
 * Implementación según INSTRUCTIVO_TURNSTILE_APPS_FINAL.md v2.0.
 * - NO llama window.turnstile.execute() (solo modo Invisible).
 * - NO pasa size ni execution al render().
 * - Contenedor siempre visible (display:flex).
 * - SiteKey consultada al backend en cada montaje.
 *
 * Props:
 *   onToken(token: string)   — se llama cuando Turnstile emite un token válido.
 *   onExpire()               — se llama cuando el token expira (opcional).
 *   onError(code: string)    — se llama si Turnstile falla (opcional).
 *   action(string)           — etiqueta para los logs de Cloudflare (ej: 'login').
 */

import { useEffect, useRef } from 'react';

const ECOLAB_API = process.env.REACT_APP_ECOLAB_API_URL ||
  'https://ecolab-backend-723916953839.us-central1.run.app';

/** Espera a que window.turnstile esté disponible (cargado por el script async). */
function waitForTurnstile(retries = 50) {
  return new Promise((resolve, reject) => {
    const check = (n) => {
      if (window.turnstile) return resolve();
      if (n <= 0) return reject(new Error('Cloudflare Turnstile no cargó'));
      setTimeout(() => check(n - 1), 100);
    };
    check(retries);
  });
}

export default function TurnstileWidget({ onToken, onExpire, onError, action = 'login' }) {
  const containerRef = useRef(null);
  const widgetIdRef  = useRef(null);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        // 1. Obtener sitekey del backend (nunca hardcodeada)
        const resp = await fetch(`${ECOLAB_API}/api/ecolab/auth/turnstile-sitekey`);
        if (!resp.ok) throw new Error(`No se pudo obtener la sitekey (${resp.status})`);
        const { siteKey } = await resp.json();

        // 2. Esperar a que el script de Cloudflare esté listo
        await waitForTurnstile();

        if (!mounted || !containerRef.current) return;

        // 3. Renderizar en modo Managed (sin size ni execution)
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          // callback: emitido cuando Turnstile valida al usuario
          callback: (token) => {
            if (mounted) onToken(token);
          },
          // expired-callback: el token expiró — resetear estado
          'expired-callback': () => {
            if (mounted) {
              onToken('');
              if (onExpire) onExpire();
              if (widgetIdRef.current != null) {
                window.turnstile.reset(widgetIdRef.current);
              }
            }
          },
          // error-callback: fallo del challenge (ej: sin conexión)
          'error-callback': (code) => {
            if (mounted && onError) onError(code);
          },
        });
        // NO se llama window.turnstile.execute() — solo aplica en modo Invisible
      } catch (err) {
        console.warn('[TurnstileWidget] Error al inicializar:', err.message);
        if (mounted && onError) onError(err.message);
      }
    };

    init();

    return () => {
      mounted = false;
      // Limpiar el widget al desmontar
      if (widgetIdRef.current != null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* ignore */ }
        widgetIdRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    // Contenedor VISIBLE — modo Managed requiere espacio real en el DOM.
    // NO usar display:none ni visibility:hidden.
    <div
      ref={containerRef}
      style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}
    />
  );
}
